import * as M from "../data/model";
import { assert } from "../utils/debug";

export interface SectionDefinition extends CommonDefinition {
    sections: (SectionDefinition | ProductDefinition)[];
}

export interface ProductDefinition extends CommonDefinition {
    application: string;
    category: DefinitionIdentifier;
    line: DefinitionIdentifier;
    riskName: string;
}

export interface CommonDefinition {
    name: DefinitionIdentifier;
    multi: boolean;
    visibility: (ui: UserInfo, cp?: M.ConsultationProcess) => boolean;
}

export interface DefinitionIdentifier {
    key: string;
    displayName: string;
    printName: string;
    icon?: string;
}

export function isSectionDefinition(
    def: SectionDefinition | ProductDefinition,
): def is SectionDefinition {
    return (def as any).sections !== undefined;
}

export function isProductDefinition(
    def: SectionDefinition | ProductDefinition,
): def is ProductDefinition {
    return !isSectionDefinition(def);
}

export class UserInfo {
    protected orgCode: string;
    protected vw: string;
    protected zg: string;
    protected roles: any[];
    protected supportedUserTypes: UserType[];

    constructor(token: any) {
        if (token.ou) {
            this.orgCode = token.ou.match(/[^-]+/)[0];
        }
        this.vw = token.vw;
        this.zg = token.zg;
        this.roles = (token.realm_access && token.realm_access.roles) || [];
        this.supportedUserTypes = Object.values(UserType).filter(
            (ut: UserType) => this.isUserType(ut),
        );
    }

    private hasRole(roleName) {
        roleName = (roleName || "").toLowerCase();
        return this.roles.find(
            (role: string) => role.toLowerCase() === roleName,
        );
    }

    public estimateTenantsAndUserTypes() {
        const tenants: M.TenantType[] = [];
        const userTypes: UserType[] = [];
        if (
            this.vw === "AOZ" ||
            this.vw === "AODH" ||
            this.hasRole("App_ZAS_Experte_ZEP")
        ) {
            tenants.push("Zurich");
            userTypes.push("ZEP");
        }
        if (this.vw === "Bonnfinanz" || this.hasRole("App_ZAS_Experte_BoFi")) {
            tenants.push("BoFi");
            userTypes.push("BoFi");
        }
        if (this.orgCode === "DI") {
            tenants.push("DA");
            userTypes.push("DA-GST");
        }
        if (this.orgCode === "TD") {
            if (tenants.slice(-1)[0] !== "DA") {
                tenants.push("DA");
            }
            userTypes.push("DA-TDG");
        }
        if (
            (this.vw === "Finanzberater" && this.zg === "Hauptberuf") ||
            this.hasRole("App_ZAS_Experte_DeuBa_Finanzberater")
        ) {
            tenants.push("DB");
            userTypes.push("DB-FB");
        }
        if (
            (this.vw === "Finanzberater" && this.zg === "Innendienst") ||
            this.hasRole("App_ZAS_Experte_DeuBa_VSLSupport")
        ) {
            if (tenants.slice(-1)[0] !== "DB") {
                tenants.push("DB");
            }
            userTypes.push("DB-VSL");
        }
        if (
            this.hasRole("App_ZAS_Experte_Makler") ||
            this.hasRole("App_ZAS_Experte_Makler_Pool_GI")
        ) {
            tenants.push("makler");
        }
        return { tenants, userTypes };
    }

    public isZEP(): boolean {
        return this.vw === "AOZ" || this.vw === "AODH";
    }

    public isBoFi(): boolean {
        return this.vw === "Bonnfinanz";
    }

    public isDa(): boolean {
        return this.isDaGst() || this.isDaTdg();
    }

    public isDaGst(): boolean {
        return this.orgCode === "DI";
    }

    public isDaTdg(): boolean {
        return this.orgCode === "TD";
    }

    public isDb(): boolean {
        return this.isDbFinanz() || this.isDbVsl();
    }

    public isDbFinanz(): boolean {
        return this.vw === "Finanzberater" && this.zg === "Hauptberuf";
    }

    public isDbVsl(): boolean {
        return this.vw === "Finanzberater" && this.zg === "Innendienst";
    }

    public isUserType(ut: UserType) {
        switch (ut) {
            case "ZEP":
                return this.isZEP();
            case "BoFi":
                return this.isBoFi();
            case "DA":
                return this.isDa();
            case "DA-GST":
                return this.isDaGst();
            case "DA-TDG":
                return this.isDaTdg();
            case "DB":
                return this.isDb();
            case "DB-FB":
                return this.isDbFinanz();
            case "DB-VSL":
                return this.isDbVsl();
            default:
                assert(false, `Missing UserType: "${ut}"`);
                return false;
        }
    }

    public allSupportedUserTypes(): UserType[] {
        return this.supportedUserTypes;
    }
}

export type UserType =
    | "ZEP"
    | "BoFi"
    | "DA"
    | "DA-GST"
    | "DA-TDG"
    | "DB"
    | "DB-FB"
    | "DB-VSL";
export const UserType = {
    ZEP: "ZEP" as UserType,
    BoFi: "BoFi" as UserType,
    DA: "DA" as UserType,
    DAGST: "DA-GST" as UserType,
    DATDG: "DA-TDG" as UserType,
    DB: "DB" as UserType,
    DBFB: "DB-FB" as UserType,
    DBVSL: "DB-VSL" as UserType,
};

export function notFor(...userTypes: UserType[]) {
    return (ui: UserInfo): boolean => {
        return !userTypes
            .map((ut: UserType) => {
                switch (ut) {
                    case "ZEP":
                        return ui.isZEP();
                    case "BoFi":
                        return ui.isBoFi();
                    case "DA":
                        return ui.isDa();
                    case "DA-GST":
                        return ui.isDaGst();
                    case "DA-TDG":
                        return ui.isDaTdg();
                    case "DB":
                        return ui.isDb();
                    case "DB-FB":
                        return ui.isDbFinanz();
                    case "DB-VSL":
                        return ui.isDbVsl();
                    default:
                        assert(false, `Missing UserType: "${ut}"`);
                        return false;
                }
            })
            .reduce((a, b) => a || b);
    };
}

export function onlyFor(...userTypes: UserType[]) {
    const notForTestFn = notFor(...userTypes);
    return (ui: UserInfo) => !notForTestFn(ui);
}

export function createProductsByKey(
    root: SectionDefinition,
): Record<string, ProductDefinition> {
    const dict = {};
    addByKey(dict, root);
    return dict;
}

function addByKey(
    dict: Record<string, ProductDefinition>,
    def: SectionDefinition | ProductDefinition,
) {
    if (isProductDefinition(def)) {
        dict[def.name.key] = def;
    } else {
        def.sections.forEach((sec) => addByKey(dict, sec));
    }
}
